import { FormControl, FormErrorMessage, Flex } from "@chakra-ui/react";
import { FieldValues, FormProvider, useForm } from "react-hook-form";
import PrimaryButton from "../PrimaryButton";
import Input from "../inputs/Input";
import Select from "../inputs/Select";
import PasswordInput from "../inputs/PasswordInput";
import { User } from "../../pages/SignInPage";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

type CreateReferentUserFormProps = {
  onSubmit: (user: User) => void;
};

const referentUserSchema = z.object({
  firstName: z.string().min(1, { message: "Ce champ est requis." }),
  lastName: z.string().min(1, { message: "Ce champ est requis." }),
  phoneNumber: z.string().optional(),
  email: z
    .string()
    .min(1, { message: "Ce champ est requis." })
    .email({ message: "Doit être un email valide." }),
  password: z.string().min(1, { message: "Ce champ est requis." }),
  role: z.string().min(1, { message: "Ce champ est requis." }),
  access: z.string().min(1, { message: "Ce champ est requis." }),
});

function CreateReferentUserForm({ onSubmit }: CreateReferentUserFormProps) {
  const methods = useForm({
    resolver: zodResolver(referentUserSchema),
  });
  const transformFields = (fields: FieldValues) => {
    return {
      id: fields.id,
      firstName: fields.firstName,
      lastName: fields.lastName,
      phoneNumber: fields.phoneNumber,
      email: fields.email,
      password: fields.password,
      role: fields.role,
      access: fields.access,
    };
  };
  const handleSubmit = async () => {
    try {
      const valid = await methods.trigger();
      if (valid && onSubmit) {
        onSubmit(transformFields(methods.getValues()));
      }
    } catch (err) {
      // Todo: Sentry
    }
  };

  return (
    <Flex w="100%">
      <FormProvider {...methods}>
        <form style={{ marginTop: "32px", width: "100%" }}>
          <Flex direction="column" gap="24px">
            <FormControl isInvalid={!!methods.formState.errors.lastName}>
              <Input
                id="lastName"
                placeholder="Nom"
                {...methods.register("lastName", {
                  required: "Ce champ est obligatoire",
                })}
              ></Input>
              <FormErrorMessage>
                <>{methods.formState.errors?.lastName?.message}</>
              </FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={!!methods.formState.errors.firstName}>
              <Input
                id="firstName"
                placeholder="Prénom"
                {...methods.register("firstName", {
                  required: "Ce champ est obligatoire",
                })}
              ></Input>
              <FormErrorMessage>
                <>{methods.formState.errors?.firstName?.message}</>
              </FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={!!methods.formState.errors.phoneNumber}>
              <Input
                id="phoneNumber"
                placeholder="Téléphone (facultatif)"
                {...methods.register("phoneNumber")}
              ></Input>
              <FormErrorMessage>
                <>{methods.formState.errors?.phoneNumber?.message}</>
              </FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={!!methods.formState.errors.email}>
              <Input
                id="email"
                placeholder="Email"
                {...methods.register("email", {
                  required: "Ce champ est obligatoire",
                })}
              ></Input>
              <FormErrorMessage>
                <>{methods.formState.errors?.email?.message}</>
              </FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={!!methods.formState.errors.password}>
              <PasswordInput
                id="password"
                placeholder="Password"
                type="password"
                {...methods.register("password", {
                  required: "Ce champ est obligatoire",
                })}
              ></PasswordInput>
              <FormErrorMessage>
                <>{methods.formState.errors?.password?.message}</>
              </FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={!!methods.formState.errors.role}>
              <Select
                id="role"
                placeholder="Rôle"
                defaultValue={"AUDIOPROSTHESIST"}
                {...methods.register("role", {
                  required: "Ce champ est obligatoire",
                })}
              >
                <option value="AUDIOPROSTHESIST">Audioprothésiste</option>
                <option value="ASSISTANT">Assistant</option>
                <option value="OTHER">Autre</option>
              </Select>
              <FormErrorMessage>
                <>{methods.formState.errors?.role?.message}</>
              </FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={!!methods.formState.errors.access}>
              <Select
                id="access"
                placeholder="Accès"
                defaultValue="ADMIN"
                {...methods.register("access", {
                  required: "Ce champ est obligatoire",
                })}
              >
                <option value="ADMIN">Administrateur du centre</option>
                <option value="MEMBER">Membre</option>
              </Select>
              <FormErrorMessage>
                <>{methods.formState.errors?.access?.message}</>
              </FormErrorMessage>
            </FormControl>

            <PrimaryButton type="button" onClick={handleSubmit}>
              Continuer
            </PrimaryButton>
          </Flex>
        </form>
      </FormProvider>
    </Flex>
  );
}

export default CreateReferentUserForm;
